/* eslint-disable n/no-callback-literal */
import { CustomFieldCustomization, HandsontableCore, HandsontableNamespace } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators';
import { isNumberValid, isUrlValidAndSafe, pointNumberValid } from '../../helpers/strings';

import { delay } from '../../helpers/utils';
import { validateDate } from '../../helpers/date';
import { validateTag } from '../hot-points';

// Allows us to use `Handsontable` as a global.
declare const Handsontable: HandsontableNamespace;

@customElement('handsontable-element')
export class HandsontableElement extends LitElement {
    realContainerElement!: HTMLDivElement;

    @property({ type: Array })
    data: any[] = [];

    @property({ type: Object })
    settings: any = {
        data: this.data,
        rowHeaders: true,
        colHeaders: true,
        height: 500,
        licenseKey: '50d26-41edc-5dd30-3402e-65c5e',
    };

    hotReady = false;

    // SAFETY: The logic below ensures that `hot` is defined by the time it is used.
    hot!: HandsontableCore;
    lastBoundingRect = new DOMRect(1, 1, 1, 1);

    static isLoading = false;
    static hasEverLoaded = false;

    async firstUpdated(_changedProperties: any) {
        await this.globalSetup();
        await this.startup();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.visibilityAdapter(); // Do NOT await this, it runs continuously
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.menuFixAdapter(); // Do NOT await this, it runs continuously
    }

    // Loads handsontable, and sets everything up.
    async globalSetup() {
        if (HandsontableElement.hasEverLoaded || HandsontableElement.isLoading) {
            return;
        }

        HandsontableElement.isLoading = true;

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://cdn.jsdelivr.net/npm/handsontable/dist/handsontable.full.js';

        document.head.appendChild(script);

        const style = document.createElement('link');
        style.rel = 'stylesheet';
        style.href = 'https://cdn.jsdelivr.net/npm/handsontable/dist/handsontable.full.min.css';

        // create custom styles for our use cases
        const custom_styles = document.createElement('style');
        custom_styles.innerHTML = `
            .handsontable td, .handsontable th {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
            }
            .htInvalid {
                background-color: #ffebee !important;
                border: 1px solid #f44336 !important;
                color: #d32f2f !important;
            }
        `;


        document.head.appendChild(style);
        document.head.appendChild(custom_styles);

        // Wait for JS to load.  Need to look in window for the `Handsontable` global.
        while ((window as any).Handsontable === undefined) {
            await delay(100);
        }

        HandsontableElement.hasEverLoaded = true;
    }

    // After globalSetup, this is called to actually create the table, and update it.
    async startup() {
        while (!HandsontableElement.hasEverLoaded) {
            await delay(100);
        }

        this.hot = new Handsontable(this.realContainerElement, {licenseKey: '179dc-b1c7d-0d543-34b22-d6e54', height:200});
        this.hotReady = true;
        this.updateTable();

        // Emit custom event when the table is ready.
        this.dispatchEvent(new CustomEvent('ready'));
    }

    // The handsontable element is "fake", and must be laid out in its proper
    // location.  This method ensures that that table stays in the right place.
    private async visibilityAdapter() {
        let lastWindowWidth = 0;
        let lastWindowHeight = 0;

        const startTime = Date.now();

        while (true) {
            await delay(10);

            while (!this.realContainerElement.isConnected) {
                await delay(1000); // wait for 1 second before checking again
            }

            const currentWindowWidth = window.innerWidth;
            const currentWindowHeight = window.innerHeight;

            const internalContainerElement = this.renderRoot as HTMLElement;
            const internalBoundingRect = internalContainerElement.getBoundingClientRect();
            internalContainerElement.style.display = 'block';

            // skip this for the first 15 seconds because often things are still getting set.
            // without this we end up with a blank screen until someone resizes
            if ((Date.now() - startTime) > 15000 && lastWindowWidth === currentWindowWidth && lastWindowHeight === currentWindowHeight) {
                continue;
            }

            lastWindowWidth = currentWindowWidth;
            lastWindowHeight = currentWindowHeight;


            this.lastBoundingRect = internalBoundingRect;

            // First, make sure the real element is positioned in the right place.
            this.realContainerElement.style.left = internalBoundingRect.left.toString() + 'px';
            this.realContainerElement.style.top = internalBoundingRect.top.toString() + 'px';
            this.realContainerElement.style.position = 'absolute';

            // update hot settings with width
            this.hot.updateSettings({width: internalBoundingRect.width, height: internalBoundingRect.height});

            // // Next, make sure the internal element has the right height / width.
            internalContainerElement.style.width = '100%';
            internalContainerElement.style.height = '100%';

            // Make sure the table / menus are not inert.
            try {
                this.realContainerElement.attributes.removeNamedItem('inert');
            } catch (_) {}
        }
    }

    async menuFixAdapter() {
        while (true) {
            await delay(100);

            if (!this.realContainerElement.isConnected) {
                return;
            }

            try {
                document.querySelectorAll('.htMenu').forEach((element: any) => {
                    element.attributes.removeNamedItem('inert');
                });
            } catch (_) {}
        }
    }

    connectedCallback() {
        this.realContainerElement = document.createElement('div');
        document.body.appendChild(this.realContainerElement);

        super.connectedCallback();
    }

    disconnectedCallback() {
        this.realContainerElement.remove();
        super.disconnectedCallback();
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
        if (changedProperties.has('data') || changedProperties.has('settings')) {
            this.updateTable();
        }
    }

    updateTable() {
        if (!this.hotReady) {
            return;
        }

        this.hot.updateData(this.data);
        this.hot.updateSettings(this.settings);
        this.hot.render();
    }

    render() {
        return html``;
    }

    static get styles() {
        return css`
            :host {
                position: relative;
                width: max(100%, 200px);
                height: max(100%, 200px);
                display: block;
            }
        `;
    }

    protected createRenderRoot() {
        return this;
    }

    isVisible(elem: HTMLElement) {
        const rect = elem.getBoundingClientRect();
        return rect.width !== 0 && rect.height !== 0;
    }
}

export function getMaxLengthValidator(length: number) {
    return (value: string | undefined, callback: (valid: boolean) => void) => {
        // eslint-disable-next-line n/no-callback-literal
        callback((value ?? '').length <= length);
    };
}

export function getDateValidator() {
    return (value: string | undefined, callback: (valid: boolean) => void) => {
        callback(value === undefined || validateDate(value));
    };
}

export function getTagValidator() {
    return (value: string | undefined, callback: (valid: boolean) => void) => {
        callback(value === undefined || validateTag(value));
    };
}

export function getPointNumberValidator() {
    return (value: string | undefined, callback: (valid: boolean) => void) => {
        callback(value === undefined || pointNumberValid(value));
    };
}

export function getCustomFieldValidator(field: CustomFieldCustomization) {
    return (value: string | undefined, callback: (valid: boolean) => void) => {
        const isRequired = field.customizations?.mandatory ?? false;
        if ('Text' in field.field_type) {
            // check that the value is not empty if the field is required
            if (isRequired) {
                callback((value ?? '').length > 0);
            } else {
                callback(true);
            }
        } else if ('Number' in field.field_type) {
            if (isRequired) {
                callback(value !== undefined && isNumberValid(value));
            } else {
                callback(value === undefined || isNumberValid(value));
            }
        } else if ('Phone' in field.field_type) {
            if (isRequired) {
                callback((value ?? '').length > 0);
            } else {
                callback(true);
            }
        } else if ('URL' in field.field_type) {
            if (isRequired) {
                const isValid = isUrlValidAndSafe(value, false);
                callback(isValid);
            } else {
                const isValid = isUrlValidAndSafe(value, true);
                callback(isValid);
            }
        } else if ('MultiSelect' in field.field_type) {
            const multiselecttype = field.field_type.MultiSelect;
            if (isRequired) {
                callback((value ?? '').length > 0 && multiselecttype.options.includes(value ?? ''));
            } else {
                callback(value === undefined || multiselecttype.options.includes(value));
            }
        } else if ('SingleSelect' in field.field_type) {
            const singleselecttype = field.field_type.SingleSelect;
            if (isRequired) {
                callback((value ?? '').length > 0 && singleselecttype.options.includes(value ?? ''));
            } else {
                callback(value === undefined || singleselecttype.options.includes(value));
            }
        } else {
            callback(true);
        }
    };
}

export function getCannotBeEmptyValidator() {
    return (value: string | undefined, callback: (valid: boolean) => void) => {
        // eslint-disable-next-line n/no-callback-literal
        callback((value ?? '').length > 0);
    };
}